import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    SimpleGrid,
    Card,
    Text,
    VStack,
    HStack,
    Badge,
    Button,
    Heading,
    Container,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorModeValue,
    Spinner,
    Center,
    Flex,
    Icon,
    Tooltip
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SignalRContext } from "./SignalRContext";
import { ChevronLeftIcon, WarningIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { FaWater, FaTint } from "react-icons/fa";

// Use default export instead of named export
const FaultPumpDetails = () => {
    const { message } = useContext(SignalRContext);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [faultyPumps, setFaultyPumps] = useState([]);
    const [faultySaltPumps, setFaultySaltPumps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allDevices, setAllDevices] = useState([]);

    // Status label configurations (same as in PumpDevice and SaltPumpDevice)
    const getFreshWaterStatusLabels = (battery) => {
        let pump = Math.floor(battery / 10);
        if (pump === 9) return [{ id: 1, label: "DIRECT FEED", bitPosition: 0, positiveIsGood: false }];
        if (pump === 7) return null;
        if (pump === 3) return [
            { id: 1, label: "控制電源", bitPosition: 8, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 6, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 5, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 4, positiveIsGood: false }
        ];
        if (pump === 2) return [
            { id: 1, label: "控制電源", bitPosition: 5, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 4, positiveIsGood: false }
        ];
        if (pump === 1) return [
            { id: 1, label: "一號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 4, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 0, positiveIsGood: false }
        ];
        return [
            { id: 1, label: "控制電源", bitPosition: 8, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 6, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 5, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 4, positiveIsGood: false }
        ];
    };

    const getSaltWaterStatusLabels = (battery) => {
        const unitDigit = Math.abs(battery) % 10;
        if (unitDigit === 9) return [{ id: 1, label: "Direct Feed", bitPosition: 0, positiveIsGood: true }];
        if (unitDigit === 7) return null;
        if (unitDigit === 3) return [
            { id: 1, label: "控制電源", bitPosition: 17, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 16, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 15, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 14, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 13, positiveIsGood: false }
        ];
        if (unitDigit === 2) return [
            { id: 1, label: "控制電源", bitPosition: 14, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 13, positiveIsGood: false }
        ];
        if (unitDigit === 1) return [
            { id: 1, label: "一號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 13, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 9, positiveIsGood: false }
        ];
        if (unitDigit === 5) return [
            { id: 1, label: "控制電源", bitPosition: 12, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 8, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 7, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 11, positiveIsGood: false }
        ];
        if (unitDigit === 4) return [
            { id: 1, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 8, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false }
        ];
        return [
            { id: 1, label: "控制電源", bitPosition: 17, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 16, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 15, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 14, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 13, positiveIsGood: false }
        ];
    };

    const checkForFaults = (device, statusLabels) => {
        if (!statusLabels || !device.status) return [];

        const faults = [];
        statusLabels.forEach(label => {
            if (!label.positiveIsGood) {
                const bitValue = device.status.charAt(label.bitPosition);
                if (bitValue === '1') {
                    faults.push({
                        label: label.label,
                        bitPosition: label.bitPosition,
                        deviceName: device.deviceName,
                        block: device.block,
                        deviceType: device.deviceType,
                        lastUpdated: device.lastUpdated
                    });
                }
            }
        });

        return faults;
    };

    const analyzePumps = (devices) => {
        const freshWaterFaults = [];
        const saltWaterFaults = [];

        devices.forEach(device => {
            if (device.deviceType === 2 || device.deviceType === 3) {
                // Check fresh water pump faults
                const freshLabels = getFreshWaterStatusLabels(device.battery || 0);
                const freshFaults = checkForFaults(device, freshLabels);
                if (freshFaults.length > 0) {
                    freshWaterFaults.push({
                        device,
                        faults: freshFaults
                    });
                }

                // Check salt water pump faults
                const saltLabels = getSaltWaterStatusLabels(device.battery || 0);
                const saltFaults = checkForFaults(device, saltLabels);
                if (saltFaults.length > 0) {
                    saltWaterFaults.push({
                        device,
                        faults: saltFaults
                    });
                }
            }
        });

        return { freshWaterFaults, saltWaterFaults };
    };

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                setLoading(true);
                const response = await fetch('/Devices/GetDevicesOnAllBlocks');
                if (response.ok) {
                    const devices = await response.json();
                    setAllDevices(devices);
                    const { freshWaterFaults, saltWaterFaults } = analyzePumps(devices);
                    setFaultyPumps(freshWaterFaults);
                    setFaultySaltPumps(saltWaterFaults);
                }
            } catch (error) {
                console.error('Error fetching devices:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDevices();
    }, []);

    useEffect(() => {
        if (message && message.flag === true && Array.isArray(message.devices)) {
            const updatedDevices = message.devices;
            setAllDevices(prevDevices => {
                const mergedDevices = prevDevices.map(device => {
                    const updatedDevice = updatedDevices.find(d => d.deviceId === device.deviceId);
                    return updatedDevice ? { ...device, ...updatedDevice } : device;
                });

                // Re-analyze for faults
                const { freshWaterFaults, saltWaterFaults } = analyzePumps(mergedDevices);
                setFaultyPumps(freshWaterFaults);
                setFaultySaltPumps(saltWaterFaults);

                return mergedDevices;
            });
        }
    }, [message]);

    const getLocationText = (deviceType) => {
        return deviceType === 2 ? "地面" : "天台";
    };

    const getPumpTypeText = (device) => {
        const location = getLocationText(device.deviceType);
        return `${location}食水泵`;
    };

    const getSaltPumpTypeText = (device) => {
        const location = getLocationText(device.deviceType);
        return `${location}沖廁水泵`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleString('zh-HK');
        } catch {
            return dateString;
        }
    };

    const handleBack = () => {
        navigate("/dashboard");
    };

    const totalFaults = faultyPumps.reduce((sum, pump) => sum + pump.faults.length, 0) +
        faultySaltPumps.reduce((sum, pump) => sum + pump.faults.length, 0);

    const cardBg = useColorModeValue("white", "gray.700");
    const headerBg = useColorModeValue("blue.500", "blue.600");
    const warningBg = useColorModeValue("red.50", "red.900");

    if (loading) {
        return (
            <Center h="400px">
                <VStack>
                    <Spinner size="xl" color="blue.500" />
                    <Text>載入中...</Text>
                </VStack>
            </Center>
        );
    }

    return (
        <Container maxW="container.xl" py={6}>
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
                <HStack>
                    <Button leftIcon={<ChevronLeftIcon />} onClick={handleBack} variant="outline">
                        返回儀表板
                    </Button>
                    <Heading size="lg">水泵故障詳細信息</Heading>
                </HStack>
                <Badge colorScheme={totalFaults > 0 ? "red" : "green"} fontSize="lg" p={2}>
                    總故障數: {totalFaults}
                </Badge>
            </Flex>

            {/* Summary Alert */}
            {totalFaults === 0 ? (
                <Alert status="success" borderRadius="md" mb={6}>
                    <CheckCircleIcon />
                    <AlertTitle ml={2}>所有水泵運行正常</AlertTitle>
                    <AlertDescription>目前沒有檢測到任何故障</AlertDescription>
                </Alert>
            ) : (
                <Alert status="error" borderRadius="md" mb={6}>
                    <WarningIcon />
                    <AlertTitle ml={2}>檢測到 {totalFaults} 個故障</AlertTitle>
                    <AlertDescription>請立即檢查相關設備</AlertDescription>
                </Alert>
            )}

            <SimpleGrid columns={1} spacing={6}>
                {/* Fresh Water Pump Faults */}
                <Card bg={cardBg} boxShadow="lg" borderRadius="lg" overflow="hidden">
                    <Box bg={headerBg} px={6} py={3}>
                        <HStack>
                            <Icon as={FaWater} color="white" />
                            <Heading size="md" color="white">
                                食水泵故障
                            </Heading>
                            <Badge colorScheme="red" fontSize="md">
                                {faultyPumps.reduce((sum, pump) => sum + pump.faults.length, 0)} 個故障
                            </Badge>
                        </HStack>
                    </Box>

                    <Box p={6}>
                        {faultyPumps.length === 0 ? (
                            <Alert status="success">
                                <CheckCircleIcon />
                                <AlertTitle ml={2}>食水泵運行正常</AlertTitle>
                            </Alert>
                        ) : (
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>位置</Th>
                                        <Th>設備名稱</Th>
                                        <Th>故障類型</Th>
                                        <Th>最後更新</Th>
                                        <Th>狀態</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {faultyPumps.flatMap((pumpData, index) =>
                                        pumpData.faults.map((fault, faultIndex) => (
                                            <Tr key={`${index}-${faultIndex}`} bg={warningBg}>
                                                <Td fontWeight="medium">
                                                    <HStack>
                                                        <Box w="3" h="3" bg="red.500" borderRadius="full" />
                                                        <Text>{getPumpTypeText(pumpData.device)}</Text>
                                                        <Text color="gray.500">({pumpData.device.block}座)</Text>
                                                    </HStack>
                                                </Td>
                                                <Td>{pumpData.device.deviceName}</Td>
                                                <Td>
                                                    <HStack>
                                                        <WarningIcon color="red.500" />
                                                        <Text fontWeight="bold" color="red.600">
                                                            {fault.label}
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                                <Td>{formatDate(pumpData.device.lastUpdated)}</Td>
                                                <Td>
                                                    <Badge colorScheme="red" variant="solid">
                                                        故障
                                                    </Badge>
                                                </Td>
                                            </Tr>
                                        ))
                                    )}
                                </Tbody>
                            </Table>
                        )}
                    </Box>
                </Card>

                {/* Salt Water Pump Faults */}
                <Card bg={cardBg} boxShadow="lg" borderRadius="lg" overflow="hidden">
                    <Box bg="teal.500" px={6} py={3}>
                        <HStack>
                            <Icon as={FaTint} color="white" />
                            <Heading size="md" color="white">
                                沖廁水泵故障
                            </Heading>
                            <Badge colorScheme="red" fontSize="md">
                                {faultySaltPumps.reduce((sum, pump) => sum + pump.faults.length, 0)} 個故障
                            </Badge>
                        </HStack>
                    </Box>

                    <Box p={6}>
                        {faultySaltPumps.length === 0 ? (
                            <Alert status="success">
                                <CheckCircleIcon />
                                <AlertTitle ml={2}>沖廁水泵運行正常</AlertTitle>
                            </Alert>
                        ) : (
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>位置</Th>
                                        <Th>設備名稱</Th>
                                        <Th>故障類型</Th>
                                        <Th>最後更新</Th>
                                        <Th>狀態</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {faultySaltPumps.flatMap((pumpData, index) =>
                                        pumpData.faults.map((fault, faultIndex) => (
                                            <Tr key={`salt-${index}-${faultIndex}`} bg={warningBg}>
                                                <Td fontWeight="medium">
                                                    <HStack>
                                                        <Box w="3" h="3" bg="red.500" borderRadius="full" />
                                                        <Text>{getSaltPumpTypeText(pumpData.device)}</Text>
                                                        <Text color="gray.500">({pumpData.device.block}座)</Text>
                                                    </HStack>
                                                </Td>
                                                <Td>{pumpData.device.deviceName}</Td>
                                                <Td>
                                                    <HStack>
                                                        <WarningIcon color="red.500" />
                                                        <Text fontWeight="bold" color="red.600">
                                                            {fault.label}
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                                <Td>{formatDate(pumpData.device.lastUpdated)}</Td>
                                                <Td>
                                                    <Badge colorScheme="red" variant="solid">
                                                        故障
                                                    </Badge>
                                                </Td>
                                            </Tr>
                                        ))
                                    )}
                                </Tbody>
                            </Table>
                        )}
                    </Box>
                </Card>
            </SimpleGrid>
        </Container>
    );
};

// Use default export
export default FaultPumpDetails;